import { AnyAction, Reducer } from 'redux';
import { Selector } from 'reselect';
import { State } from './types';
import { RequestDataIds } from './constants/ABA';
import {
  SET_PARAM_THEME,
  SET_PARAM_AMS_SEGMENTS,
  SET_PARAM_ASSETS_MANIFEST,
  SET_PARAM_BASE_URL,
  SET_PARAM_BRAND,
  SET_PARAM_CHANNEL,
  SET_PARAM_CLIENT_ID,
  SET_PARAM_CONTAINER,
  SET_PARAM_DEPOSIT_URL,
  SET_PARAM_LANGUAGE,
  SET_PARAM_LAYOUT_DIMENSIONS,
  SET_PARAM_PLATFORM,
  SET_PARAM_PROXY_PREFIX,
  SET_PARAM_USER_DATA,
  SET_PARAM_USER_TOKEN,
  SET_PARAM_WITHDRAWAL_URL,
  SET_PARAM_CROSS_DOMAIN_CLICK_STREAM_ID,
  SET_PARAM_BRAND_HASH,
  SET_PARAM_APPLICATION_ID,
  SET_PARAM_OPERATOR_ID,
  SET_PARAM_API_KEY,
  CHANGE_LAYOUT_DIMENSIONS,
} from './constants';

export const defaultInitialState: State = {
  [RequestDataIds.RenderContainer]: '',
  [RequestDataIds.Language]: '',
  [RequestDataIds.BaseUrl]: '',
  [RequestDataIds.Brand]: '',
  [RequestDataIds.Channel]: '',
  [RequestDataIds.ProxyPrefix]: '',
  [RequestDataIds.UserToken]: '',
  [RequestDataIds.UserData]: {} as State[RequestDataIds.UserData],
  [RequestDataIds.ClientId]: '',
  [RequestDataIds.Platform]: '',
  [RequestDataIds.DepositUrl]: '',
  [RequestDataIds.WithdrawalUrl]: '',
  [RequestDataIds.LayoutDimensions]: {
    top: '0px',
    left: '0px',
    right: '0px',
    bottom: '0px',
  },
  [RequestDataIds.AssetsManifest]: {},
  [RequestDataIds.AmsSegments]: [],
  [RequestDataIds.Theme]: '',
  [RequestDataIds.CrossDomainClickStreamId]: '',
  [RequestDataIds.BrandHash]: '',
  [RequestDataIds.ApplicationId]: '',
  [RequestDataIds.Operator]: '',
  [RequestDataIds.APIKey]: '',
};

export function createABAReducer(initialState: Partial<State> = {}): Reducer<State, AnyAction> {
  return (state: State = { ...defaultInitialState, ...initialState }, { type, payload }: AnyAction): State => {
    switch (type) {
      case SET_PARAM_CONTAINER:
        return { ...state, [RequestDataIds.RenderContainer]: payload };
      case SET_PARAM_LANGUAGE:
        return { ...state, [RequestDataIds.Language]: payload };
      case SET_PARAM_BASE_URL:
        return { ...state, [RequestDataIds.BaseUrl]: payload };
      case SET_PARAM_BRAND:
        return { ...state, [RequestDataIds.Brand]: payload };
      case SET_PARAM_CHANNEL:
        return { ...state, [RequestDataIds.Channel]: payload };
      case SET_PARAM_PROXY_PREFIX:
        return { ...state, [RequestDataIds.ProxyPrefix]: payload };
      case SET_PARAM_USER_TOKEN:
        return { ...state, [RequestDataIds.UserToken]: payload };
      case SET_PARAM_USER_DATA:
        return { ...state, [RequestDataIds.UserData]: payload };
      case SET_PARAM_CLIENT_ID:
        return { ...state, [RequestDataIds.ClientId]: payload };
      case SET_PARAM_PLATFORM:
        return { ...state, [RequestDataIds.Platform]: payload };
      case SET_PARAM_DEPOSIT_URL:
        return { ...state, [RequestDataIds.DepositUrl]: payload };
      case SET_PARAM_WITHDRAWAL_URL:
        return { ...state, [RequestDataIds.WithdrawalUrl]: payload };
      case SET_PARAM_LAYOUT_DIMENSIONS:
        return { ...state, [RequestDataIds.LayoutDimensions]: payload };
      case CHANGE_LAYOUT_DIMENSIONS:
        if (!payload) return state;
        return { ...state, [RequestDataIds.LayoutDimensions]: payload };
      case SET_PARAM_ASSETS_MANIFEST:
        return { ...state, [RequestDataIds.AssetsManifest]: payload };
      case SET_PARAM_AMS_SEGMENTS:
        return { ...state, [RequestDataIds.AmsSegments]: payload };
      case SET_PARAM_THEME:
        return { ...state, [RequestDataIds.Theme]: payload };
      case SET_PARAM_CROSS_DOMAIN_CLICK_STREAM_ID:
        return { ...state, [RequestDataIds.CrossDomainClickStreamId]: payload };
      case SET_PARAM_BRAND_HASH:
        return { ...state, [RequestDataIds.BrandHash]: payload };
      case SET_PARAM_APPLICATION_ID:
        return { ...state, [RequestDataIds.ApplicationId]: payload };
      case SET_PARAM_OPERATOR_ID:
        return { ...state, [RequestDataIds.Operator]: payload };
      case SET_PARAM_API_KEY:
        return { ...state, [RequestDataIds.APIKey]: payload };
      default:
        return state;
    }
  };
}

export function getABASelectors<RS extends Record<string, any>>(
  paramsSelector: Selector<RS, State> = (state: any): State => state
) {
  const getSelector =
    <T extends RequestDataIds>(name: T) =>
    (state: RS): State[T] =>
      paramsSelector(state)?.[name];

  return {
    containerSelector: getSelector(RequestDataIds.RenderContainer),
    languageSelector: getSelector(RequestDataIds.Language),
    baseURLSelector: getSelector(RequestDataIds.BaseUrl),
    brandSelector: getSelector(RequestDataIds.Brand),
    channelSelector: getSelector(RequestDataIds.Channel),
    proxyPrefixSelector: getSelector(RequestDataIds.ProxyPrefix),
    userTokenSelector: getSelector(RequestDataIds.UserToken),
    userDataSelector: getSelector(RequestDataIds.UserData),
    clientIdSelector: getSelector(RequestDataIds.ClientId),
    platformSelector: getSelector(RequestDataIds.Platform),
    depositUrlSelector: getSelector(RequestDataIds.DepositUrl),
    withdrawalUrlSelector: getSelector(RequestDataIds.WithdrawalUrl),
    layoutDimensionsSelector: getSelector(RequestDataIds.LayoutDimensions),
    assetsManifestSelector: getSelector(RequestDataIds.AssetsManifest),
    AMSSegmentsSelector: getSelector(RequestDataIds.AmsSegments),
    themeSelector: getSelector(RequestDataIds.Theme),
    crossDomainClickStreamIdSelector: getSelector(RequestDataIds.CrossDomainClickStreamId),
    brandHashSelector: getSelector(RequestDataIds.BrandHash),
    applicationIdSelector: getSelector(RequestDataIds.ApplicationId),
    operatorSelector: getSelector(RequestDataIds.Operator),
    apiKeySelector: getSelector(RequestDataIds.APIKey),
  };
}
